/**
 * Resource Preview Component
 * Gère la prévisualisation des fichiers dans une iframe modale
 */

class ResourcePreview extends HTMLElement {
    constructor() {
        super();
    }

    /**
     * Appelé quand l'élément est ajouté au DOM
     */
    connectedCallback() {
        this.attachEventListeners();
        this.fetchFileSize();

        // Initialiser les listeners globaux une seule fois
        ResourcePreview.setupGlobalListeners();
    }

    /**
     * Récupère automatiquement la taille du fichier
     */
    async fetchFileSize() {
        const fileUrl = this.getAttribute('data-file-url');
        const autoSize = this.getAttribute('data-auto-size') === 'true';

        console.log('fetchFileSize - fileUrl:', fileUrl);
        console.log('fetchFileSize - autoSize:', autoSize);

        if (!fileUrl || !autoSize) {
            console.log('Conditions non remplies pour récupérer la taille');
            return;
        }

        const metaElement = this.querySelector('.resource-card__meta');
        console.log('metaElement trouvé:', metaElement);

        if (!metaElement) {
            console.log('Aucun élément .resource-card__meta trouvé');
            return;
        }

        try {
            console.log('Tentative de fetch HEAD sur:', fileUrl);
            // Faire une requête HEAD pour obtenir la taille sans télécharger le fichier
            const response = await fetch(fileUrl, { method: 'HEAD' });
            console.log('Response status:', response.status);
            console.log('Response headers:', [...response.headers.entries()]);

            const contentLength = response.headers.get('Content-Length');
            console.log('Content-Length:', contentLength);

            if (contentLength) {
                const sizeInBytes = parseInt(contentLength, 10);
                const formattedSize = this.formatFileSize(sizeInBytes);
                console.log('Taille formatée:', formattedSize);

                // Récupérer le texte actuel (ex: "PDF")
                const currentText = metaElement.textContent?.trim() || '';
                console.log('Texte actuel:', currentText);

                // Si le texte existe et ne contient pas déjà un séparateur
                if (currentText && !currentText.includes('—')) {
                    metaElement.textContent = `${currentText} — ${formattedSize}`;
                } else if (!currentText) {
                    metaElement.textContent = formattedSize;
                }

                console.log('Texte mis à jour:', metaElement.textContent);
            } else {
                console.log('Pas de Content-Length dans les headers');
            }
        } catch (error) {
            // En cas d'erreur (CORS, etc.), on ignore silencieusement
            console.error('Erreur lors de la récupération de la taille:', error);
        }
    }

    /**
     * Formate la taille du fichier en unités lisibles
     * @param {number} bytes - Taille en octets
     * @returns {string} - Taille formatée (ex: "3.2 Mo")
     */
    formatFileSize(bytes) {
        if (bytes === 0) return '0 octet';

        const units = ['octets', 'Ko', 'Mo', 'Go', 'To'];
        const k = 1024;
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const size = (bytes / Math.pow(k, i)).toFixed(1);

        // Enlever le .0 si c'est un nombre entier
        const formattedSize = size.endsWith('.0') ? size.slice(0, -2) : size;

        return `${formattedSize} ${units[i]}`;
    }

    attachEventListeners() {
        const previewButton = this.querySelector('.resource-card__button--ghost');
        const downloadButton = this.querySelector('.resource-card__button--primary');

        // Bouton de prévisualisation
        if (previewButton) {
            previewButton.addEventListener('click', (e) => {
                const isDisabled = previewButton.getAttribute('aria-disabled') === 'true';
                if (isDisabled) return;

                e.preventDefault();
                const href = previewButton.getAttribute('href');
                if (href) {
                    this.openPreview(href);
                }
            });
        }

        // Bouton de téléchargement
        if (downloadButton) {
            downloadButton.addEventListener('click', (e) => {
                const isDisabled = downloadButton.getAttribute('aria-disabled') === 'true';
                if (isDisabled) return;

                const url = downloadButton.getAttribute('href');
                if (url && url !== '#') {
                    e.preventDefault();
                    this.downloadFile(url);
                }
            });
        }
    }

    /**
     * Gestion de la fermeture avec Escape (statique, une seule fois)
     */
    static setupGlobalListeners() {
        // @ts-ignore - Propriété statique ajoutée dynamiquement
        if (ResourcePreview._escapeListenerAdded) return;

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                const modal = document.getElementById('resource-preview-modal');
                if (modal && modal.classList.contains('is-active')) {
                    modal.classList.remove('is-active');
                    document.body.style.overflow = '';

                    setTimeout(() => {
                        const iframe = /** @type {HTMLIFrameElement | null} */ (
                            modal.querySelector('.resource-preview__iframe')
                        );
                        if (iframe) {
                            iframe.src = '';
                        }
                    }, 300);
                }
            }
        });

        // @ts-ignore - Propriété statique ajoutée dynamiquement
        ResourcePreview._escapeListenerAdded = true;
    }

    /**
     * Force le téléchargement du fichier
     * Compatible Safari, Chrome, Firefox, Edge
     * Utilise fetch + Blob pour contourner les restrictions CORS sur l'attribut download
     * @param {string} url - URL du fichier à télécharger
     */
    async downloadFile(url) {
        // Extraire le nom du fichier depuis l'URL
        const urlParts = url.split('/');
        const fileNameWithParams = urlParts[urlParts.length - 1];
        const fileName = fileNameWithParams ? fileNameWithParams.split('?')[0] : 'download';

        try {
            // Télécharger le fichier via fetch
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            // Convertir en Blob
            const blob = await response.blob();

            // Créer une URL Blob locale
            const blobUrl = window.URL.createObjectURL(blob);

            // Créer un lien avec l'URL Blob (fonctionne même en cross-origin)
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName || 'download';
            link.style.display = 'none';

            // Ajouter au DOM et cliquer
            document.body.appendChild(link);
            link.click();

            // Nettoyer
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            }, 200);
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);

            // Fallback : ouvrir dans un nouvel onglet
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => document.body.removeChild(link), 100);
        }
    }

    /**
     * Ouvre la prévisualisation dans une modale
     * @param {string} url - URL du fichier à prévisualiser
     */
    openPreview(url) {
        // Créer la modale si elle n'existe pas
        let modal = document.getElementById('resource-preview-modal');
        if (!modal) {
            modal = this.createModal();
            document.body.appendChild(modal);
        }

        // Mettre à jour l'URL de l'iframe
        const iframe = /** @type {HTMLIFrameElement | null} */ (
            modal.querySelector('.resource-preview__iframe')
        );
        if (iframe) {
            // Utiliser Google Docs Viewer pour les PDFs
            const isPDF = url.toLowerCase().includes('.pdf');
            if (isPDF) {
                // Encoder l'URL pour Google Docs Viewer
                iframe.src = `https://docs.google.com/viewer?url=${encodeURIComponent(
                    url
                )}&embedded=true`;
            } else {
                iframe.src = url;
            }
        }

        // Afficher la modale
        modal.classList.add('is-active');
        document.body.style.overflow = 'hidden';
    }

    closePreview() {
        const modal = document.getElementById('resource-preview-modal');
        if (modal) {
            modal.classList.remove('is-active');
            document.body.style.overflow = '';

            // Nettoyer l'iframe après l'animation
            setTimeout(() => {
                const iframe = /** @type {HTMLIFrameElement | null} */ (
                    modal.querySelector('.resource-preview__iframe')
                );
                if (iframe) {
                    iframe.src = '';
                }
            }, 300);
        }
    }

    createModal() {
        const modal = document.createElement('div');
        modal.id = 'resource-preview-modal';
        modal.className = 'resource-preview-modal';
        modal.innerHTML = `
      <div class="resource-preview__overlay"></div>
      <div class="resource-preview__container">
        <div class="resource-preview__header">
          <button type="button" class="resource-preview__close" aria-label="Fermer">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="resource-preview__content">
          <iframe class="resource-preview__iframe" frameborder="0" allowfullscreen></iframe>
        </div>
      </div>
    `;

        // Événements de fermeture
        const overlay = modal.querySelector('.resource-preview__overlay');
        const closeButton = modal.querySelector('.resource-preview__close');

        if (overlay) {
            overlay.addEventListener('click', () => this.closePreview());
        }
        if (closeButton) {
            closeButton.addEventListener('click', () => this.closePreview());
        }

        return modal;
    }
}

customElements.define('resource-preview', ResourcePreview);
